import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Col, Row } from 'reactstrap';
import {
  internalRegistrationFlags,
  RegistrationFlagModel,
} from '../../../../shared/registration/model';
import { ActionButtonModal, MaterialIcon } from '../../../components';
import { UserListModal } from '../../../components/UserListModal';
import { UserSelector } from '../../../components/UserSelector';
import { useTranslation } from '../../../translations';
import { Fetcher, useConvention, useFetcher } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { ConfigBooleanSetting } from './AbstractLiveSettings';
import { ColumnSettingMulti, DataTable } from './DataTable';

// change i18n - need to add trabslation to Flag columns
const flagColumns: ColumnSettingMulti<RegistrationFlagModel>[] = [
  {
    label: 'Short Name',
    name: 'shortName',
    type: 'text',
    // Value not allowed to be edited after it is created
    disabled: (t) => t !== undefined,
  },
  {
    name: 'description',
    type: 'text',
  },
  {
    name: 'enabledDescription',
    type: 'textarea',
  },
  {
    label: 'Sort Order',
    name: 'sortOrder',
    type: 'number',
  },
  {
    label: 'Make visible to attendees',
    name: 'visibility',
    type: 'toggle',
  },
];

export const FlagSettings: FC = () => {
  const [showUsers, setShowUsers] = useState<number | null>(null);
  const { ts } = useTranslation();

  const fetch = useFetcher(async () => await api.getRegistrationFlags());

  return (
    <Row>
      <Col className="mb-10" id="flags" xs={12}>
        {showUsers && (
          <FlagUsersModal
            close={() => {
              setShowUsers(null);
            }}
            flagId={showUsers}
          />
        )}
        {fetch.data ? (
          <DataTable<RegistrationFlagModel>
            canDelete={(t) => !internalRegistrationFlags.has(t.shortName)}
            columns={flagColumns}
            data={fetch.data}
            endpoint="/api/registrations/flags"
            refresh={fetch.refresh}
            renderExtraButtons={(t) => (
              <>
                <Button
                  className="action-list"
                  color="secondary"
                  onClick={() => {
                    setShowUsers(t.id);
                  }}
                  title={ts('list_users')}
                >
                  <MaterialIcon name="group" />
                </Button>{' '}
                {t.shortName === 'nukeme' && (
                  <>
                    <NukePiiButton refresh={fetch.refresh} />{' '}
                  </>
                )}
              </>
            )}
            title="registration_flags"
          />
        ) : (
          <Fetcher result={fetch} />
        )}
      </Col>
    </Row>
  );
};

const NukePiiButton: FC<{ readonly refresh: () => void }> = ({ refresh }) => {
  const [deleteDealers, setDeleteDealers] = useState(false);
  const [deleteVolunteers, setDeleteVolunteers] = useState(false);
  const [showIgnoredUserEntry, setShowIgnoredUserEntry] = useState(false);
  const [ignoredUsers, setIgnoredUsers] = useState<number[]>();

  const convention = useConvention();
  const { ts } = useTranslation();

  const clearPii = useCallback(async () => {
    try {
      await api.requestNukePII(convention.id, {
        allowDeleteDealers: deleteDealers,
        allowDeleteVolunteers: deleteVolunteers,
        ignoreUserId: ignoredUsers,
      });

      toast.success('Request successfully created.');
    } catch (error) {
      captureError(error as Error);
    }

    refresh();
  }, [convention.id, deleteDealers, deleteVolunteers, ignoredUsers, refresh]);

  return (
    <ActionButtonModal
      actionContent="convention.clearPii.modal.action"
      buttonContent={<MaterialIcon name="group_off" />}
      color="danger"
      id="clearPii"
      onComplete={async () => {
        await clearPii();
      }}
      title="convention.clearPii.modal.title"
    >
      {ts('convention.clearPii.modal.prompt')}
      <ConfigBooleanSetting
        description="convention.clearPii.options.dealers.description"
        displayName="convention.clearPii.options.dealers.name"
        name="deleteDealers"
        saving={false}
        update={(checked) => {
          setDeleteDealers(checked);
        }}
        value={deleteDealers}
      />
      <ConfigBooleanSetting
        description="convention.clearPii.options.volunteers.description"
        displayName="convention.clearPii.options.volunteers.name"
        name="deleteVolunteers"
        saving={false}
        update={(checked) => {
          setDeleteVolunteers(checked);
        }}
        value={deleteVolunteers}
      />
      <ConfigBooleanSetting
        description=""
        displayName="convention.clearPii.options.excluded.name"
        name="ignoreSomeUsers"
        saving={false}
        update={(checked) => {
          setShowIgnoredUserEntry(checked);
        }}
        value={showIgnoredUserEntry}
      />
      {showIgnoredUserEntry && (
        <>
          <br />
          <UserSelector selectionIdsChanged={setIgnoredUsers} />
        </>
      )}
    </ActionButtonModal>
  );
};

const FlagUsersModal: FC<{ readonly flagId: number; readonly close: () => void }> = ({
  flagId,
  close,
}) => {
  const userRequest = useFetcher(async () => {
    return await api.getRegistrationFlagUsers(flagId);
  }, [flagId]);

  if (!userRequest.success) {
    return null;
  }

  return (
    <UserListModal
      close={close}
      title="users_with_the_flag"
      userList={userRequest.data.map((t) => t.id)}
    />
  );
};
